import { ResetPasswordDialog } from '@/components/admin/ResetPasswordDialog';
import { UserFormModal } from '@/components/admin/UserFormModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';
import type { AdminUser } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { MoreHorizontal, Plus, RefreshCw, Settings } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type ModalState = { kind: 'create' } | { kind: 'edit'; user: AdminUser } | null;

export function AdminUsers() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [modal, setModal] = useState<ModalState>(null);
  const [resetResult, setResetResult] = useState<{ email: string; password: string } | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchDraft.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchDraft]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users', search],
    queryFn: () => api.adminUsersList(search ? { search } : undefined),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.adminUpdateUser>[1] }) =>
      api.adminUpdateUser(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '수정에 실패했습니다.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.adminDeleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '삭제에 실패했습니다.');
    },
  });

  const resetMutation = useMutation({
    mutationFn: (user: AdminUser) =>
      api.adminResetPassword(user.id).then((r) => ({ user, password: r.temporaryPassword })),
    onSuccess: ({ user, password }) => {
      setResetResult({ email: user.email, password });
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '비밀번호 초기화에 실패했습니다.');
    },
  });

  const users = data?.users ?? [];

  return (
    <PageShell
      header={
        <PageHeader
          title="사용자"
          count={users.length}
          description="가입된 사용자를 관리합니다. 자기 자신에 대한 권한 변경/삭제는 차단됩니다."
          actions={
            <div className="flex items-center gap-2">
              <Link to="/admin/user-sync">
                <Button variant="ghost" type="button" title="외부 시스템 동기화">
                  <RefreshCw className="size-4" />
                  동기화
                </Button>
              </Link>
              <Link to="/admin/permissions/attributes">
                <Button
                  variant="ghost"
                  type="button"
                  aria-label="사용자 속성 정의 관리"
                  title="사용자 속성 정의 (권한 메뉴)"
                >
                  <Settings className="size-4" />
                  속성 정의
                </Button>
              </Link>
              <Button onClick={() => setModal({ kind: 'create' })}>
                <Plus className="size-4" />
                사용자 추가
              </Button>
            </div>
          }
        />
      }
    >
      <div className="mb-4">
        <input
          type="text"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          placeholder="이름 또는 이메일로 검색"
          className="h-9 w-72 rounded-md border border-border bg-surface px-3 text-sm"
        />
      </div>

      {serverError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {serverError}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '사용자 목록을 불러오지 못했습니다.'}
        </p>
      ) : (
        <div className="rounded-2xl border border-border bg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
              <tr>
                <Th>이름</Th>
                <Th>이메일</Th>
                <Th>역할</Th>
                <Th>상태</Th>
                <Th>생성일</Th>
                <Th align="right">액션</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isMe={me?.id === u.id}
                  onEdit={() => setModal({ kind: 'edit', user: u })}
                  onChangeRole={() =>
                    updateMutation.mutate({
                      id: u.id,
                      body: { role: u.role === 'admin' ? 'user' : 'admin' },
                    })
                  }
                  onToggleDisabled={() =>
                    updateMutation.mutate({ id: u.id, body: { disabled: !u.disabledAt } })
                  }
                  onResetPassword={() => resetMutation.mutate(u)}
                  onDelete={() => {
                    if (window.confirm(`${u.email} 계정을 삭제하시겠습니까?`)) {
                      deleteMutation.mutate(u.id);
                    }
                  }}
                />
              ))}
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-foreground-muted py-10">
                    사용자가 없습니다.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      <UserFormModal open={!!modal} mode={modal} onClose={() => setModal(null)} />

      {resetResult ? (
        <ResetPasswordDialog
          open
          email={resetResult.email}
          temporaryPassword={resetResult.password}
          onClose={() => setResetResult(null)}
        />
      ) : null}
    </PageShell>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-2.5 font-medium', align === 'right' ? 'text-right' : 'text-left')}
    >
      {children}
    </th>
  );
}

function UserRow({
  user,
  isMe,
  onEdit,
  onChangeRole,
  onToggleDisabled,
  onResetPassword,
  onDelete,
}: {
  user: AdminUser;
  isMe: boolean;
  onEdit: () => void;
  onChangeRole: () => void;
  onToggleDisabled: () => void;
  onResetPassword: () => void;
  onDelete: () => void;
}) {
  const disabled = !!user.disabledAt;
  return (
    <tr className="border-t border-border hover:bg-surface-muted/30">
      <td className="px-4 py-3">
        <Link
          to="/admin/users/$id"
          params={{ id: user.id }}
          className="font-medium hover:underline"
        >
          {user.displayName}
        </Link>
        {isMe ? (
          <span className="ml-2 text-[10px] uppercase tracking-wider text-foreground-muted">
            (나)
          </span>
        ) : null}
      </td>
      <td className="px-4 py-3 font-mono text-xs">{user.email}</td>
      <td className="px-4 py-3">
        <RoleBadge role={user.role} />
      </td>
      <td className="px-4 py-3">
        {disabled ? (
          <span className="text-xs text-foreground-muted">비활성</span>
        ) : (
          <span className="text-xs">활성</span>
        )}
      </td>
      <td className="px-4 py-3 text-foreground-muted">{formatDate(user.createdAt)}</td>
      <td className="px-4 py-3 text-right">
        <RowMenu
          isMe={isMe}
          isAdmin={user.role === 'admin'}
          isDisabled={disabled}
          onEdit={onEdit}
          onChangeRole={onChangeRole}
          onToggleDisabled={onToggleDisabled}
          onResetPassword={onResetPassword}
          onDelete={onDelete}
        />
      </td>
    </tr>
  );
}

function RoleBadge({ role }: { role: 'admin' | 'user' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider',
        role === 'admin' ? 'bg-accent/15 text-accent' : 'bg-surface-muted text-foreground-muted',
      )}
    >
      {role}
    </span>
  );
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('ko-KR');
  } catch {
    return iso;
  }
}

function RowMenu({
  isMe,
  isAdmin,
  isDisabled,
  onEdit,
  onChangeRole,
  onToggleDisabled,
  onResetPassword,
  onDelete,
}: {
  isMe: boolean;
  isAdmin: boolean;
  isDisabled: boolean;
  onEdit: () => void;
  onChangeRole: () => void;
  onToggleDisabled: () => void;
  onResetPassword: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    window.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const right = window.innerWidth - rect.right;
      // 메뉴는 5 항목 + divider 로 약 200px. 아래 공간 부족하면 버튼 위로 뒤집는다.
      const MENU_HEIGHT_ESTIMATE = 220;
      const flipUp = window.innerHeight - rect.bottom < MENU_HEIGHT_ESTIMATE + 8;
      setPos(
        flipUp
          ? { bottom: window.innerHeight - rect.top + 4, right }
          : { top: rect.bottom + 4, right },
      );
    }
    setOpen((v) => !v);
  };

  const close = () => setOpen(false);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="액션 메뉴"
        onClick={toggle}
        className="size-8 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
      >
        <MoreHorizontal className="size-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, right: pos.right }}
              className="w-48 z-50 rounded-md border border-border bg-surface text-foreground shadow-lg py-1 text-sm text-left"
            >
              <MenuItem
                onClick={() => {
                  close();
                  onEdit();
                }}
              >
                표시 이름 편집
              </MenuItem>
              <MenuItem
                disabled={isMe}
                onClick={() => {
                  close();
                  onChangeRole();
                }}
              >
                {isAdmin ? '일반 사용자로 전환' : '관리자로 승격'}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  close();
                  onResetPassword();
                }}
              >
                비밀번호 초기화
              </MenuItem>
              <MenuItem
                disabled={isMe}
                onClick={() => {
                  close();
                  onToggleDisabled();
                }}
              >
                {isDisabled ? '활성화' : '비활성화'}
              </MenuItem>
              <div className="my-1 border-t border-border" />
              <MenuItem
                disabled={isMe}
                danger
                onClick={() => {
                  close();
                  onDelete();
                }}
              >
                삭제
              </MenuItem>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function MenuItem({
  children,
  onClick,
  disabled,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'block w-full text-left px-3 py-1.5 text-sm hover:bg-surface-muted',
        'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
        danger ? 'text-destructive' : 'text-foreground',
      )}
    >
      {children}
    </button>
  );
}
